/**
 * Board Page - Multi-Board Kanban UI
 * Responsive: Mobile drawer sidebar, touch-friendly interactions
 *
 * Architecture:
 * - URL is single source of truth for boardId, viewMode, issueId
 * - React Query for all server state (boards, board, issues, initiatives)
 * - Derived state via useMemo (no useEffect for data sync)
 * - Mutations via useCreateIssue / useMoveIssue (auto-invalidate cache)
 *
 * Props:
 * - epicFilter: Pre-select epic type filter (for /epics routes)
 * - storyFilter: Pre-select story type filter (for /stories routes)
 */

import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  type Issue,
  type Board,
} from '../api/work';
import { KanbanBoard } from '../components/work/board/KanbanBoard';
import { ListView } from '../components/work/list/ListView';
import { GanttView } from '../components/work/board/GanttView';
import { BoardHeader } from '../components/work/board/BoardHeader';
import { CreateBoardDialog } from '../components/work/board/CreateBoardDialog';
import { IssueDetailPanel } from '../components/work/detail/IssueDetailPanel';
import { FilterBar, type FilterState } from '../components/work/shared/FilterBar';
import { FilterPresetsDropdown } from '../components/shared/FilterPresetsDropdown';
import { PageLayout } from '../components/layout/PageLayout';
import { type ViewMode } from '../utils/preferences';
import { useKeyboardNavigation, TRACKER_SHORTCUTS, useSkipToContent } from '../hooks/useKeyboardNavigation';
import { announce, announceIssueCreated, announceIssueStatusChange, announceError, announceModalOpened, announceModalClosed } from '../utils/announcer';
import { useIsMobile } from '../hooks/useMediaQuery';
import { validateEpicMove } from '../components/work/shared/utils';
import { useToast } from '../contexts/ToastContext';
import { useProject } from '../contexts/ProjectContext';
import { useCurrentUserId } from '../contexts/UserContext';
import { useBoardDisplay } from '../hooks/useBoardDisplay';
import { DisplayPopover } from '../components/work/shared/DisplayPopover';
import { useBoardSelection } from '../hooks/useBoardSelection';
import { useBoardFilters } from '../hooks/useBoardFilters';
import { useFilteredIssues } from '../hooks/useFilteredIssues';
import {
  useBoard,
  useIssues,
  useInitiatives,
  useCreateIssue,
  useCreateBoard,
  useMoveIssue,
} from '../hooks/useWorkData';

interface BoardPageProps {
  epicFilter?: boolean;
  storyFilter?: boolean;
}

export function BoardPage({ epicFilter = false, storyFilter = false }: BoardPageProps) {
  // ── URL params ──
  const params = useParams<{ boardId?: string; view?: string; issueId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Context (project is set by parent ProjectRouter) ──
  const { project, shortId, buildProjectUrl, isLoading: projectsLoading } = useProject();
  const projectId = project?.id;
  const currentUserId = useCurrentUserId();
  const toast = useToast();
  const isMobile = useIsMobile();

  // ── Skip to content accessibility ──
  const skipToContentProps = useSkipToContent('main-board-content');

  // ── Data: React Query (cached, deduped, no useEffect) ──
  const { boards, boardId, isLoading: boardsLoading } = useBoardSelection(projectId);
  const { data: board = null, isLoading: boardLoading, error: boardError } = useBoard(projectId, boardId);
  const { data: issuesList = [], isLoading: issuesLoading } = useIssues(projectId, boardId);
  const { data: initiatives = [] } = useInitiatives(projectId);

  // ── Derived: issues as Record ──
  const issues = useMemo(() => {
    const map: Record<string, Issue> = {};
    for (const issue of issuesList) {
      map[issue.id] = issue;
    }
    return map;
  }, [issuesList]);

  // ── View mode: derived from URL param ──
  const viewCodeToMode: Record<string, ViewMode> = { ka: 'kanban', li: 'list', ga: 'gantt' };
  const viewModeToCode: Record<string, string> = { kanban: 'ka', list: 'li', gantt: 'ga' };

  const viewMode = useMemo((): ViewMode => {
    if (params.view) {
      if (params.view in viewCodeToMode) return viewCodeToMode[params.view];
      if (['kanban', 'list', 'gantt'].includes(params.view)) return params.view as ViewMode;
    }
    return 'kanban';
  }, [params.view]);

  // ── Filters: persisted, type constraint via sanitize (no useEffect) ──
  const {
    filters,
    replaceFilters: setFilters,
    setFilters: mergeFilters,
    presets: boardPresets,
    hasActiveFilters: hasBoardActiveFilters,
    savePreset: saveBoardPreset,
    applyPreset: applyBoardPreset,
    deletePreset: deleteBoardPreset,
  } = useBoardFilters(projectId, boardId, { epicFilter, storyFilter });

  // ── Display: sort/group/fields from URL search params ──
  const display = useBoardDisplay(projectId || 'default', boardId || 'default');

  // ── Filtered issues: pure derivation ──
  const filteredIssues = useFilteredIssues(issuesList, filters, currentUserId);

  // ── Selected issue: from URL ──
  const selectedIssue = params.issueId ?? null;

  // ── URL building helpers ──
  // Canonical route names. The short 'bo'/'ep' codes only exist as legacy
  // redirects in App.tsx, which dropped the board and card until 2026-09-27, so
  // a card click built here landed on the boards list instead of opening it.
  const getBasePath = useCallback(() => {
    if (epicFilter) return 'epics';
    if (storyFilter) return 'stories';
    return 'boards';
  }, [epicFilter, storyFilter]);

  const buildBoardUrl = useCallback((bid: string, view: string, issueId?: string | null) => {
    const basePath = getBasePath();
    const viewCode = viewModeToCode[view] || view;
    if (!shortId) {
      return issueId ? `/${basePath}/${bid}/is/${issueId}` : `/${basePath}/${bid}/${viewCode}`;
    }
    if (issueId) {
      return buildProjectUrl(`${basePath}/${bid}/is/${issueId}`);
    }
    return buildProjectUrl(`${basePath}/${bid}/${viewCode}`);
  }, [shortId, buildProjectUrl, getBasePath]);

  // ── Mutations ──
  const createIssueMutation = useCreateIssue();
  const createBoardMutation = useCreateBoard();
  const moveIssueMutation = useMoveIssue();

  // ── Create-board dialog state ──
  const [createBoardOpen, setCreateBoardOpen] = useState(false);

  // ── Handlers ──
  const handleViewChange = useCallback((newView: ViewMode) => {
    const newPath = buildBoardUrl(boardId, newView, selectedIssue);
    navigate(newPath, { replace: true });
    announce(`Switched to ${newView} view`);
  }, [boardId, selectedIssue, buildBoardUrl, navigate]);

  const handleBoardChange = useCallback((newBoardId: string) => {
    navigate(buildBoardUrl(newBoardId, viewMode));
  }, [buildBoardUrl, viewMode, navigate]);

  const handleCreateBoard = useCallback(async (name: string, description: string) => {
    if (!projectId) return;
    const newBoard = await createBoardMutation.mutateAsync({ projectId, data: { name, description } });
    navigate(buildBoardUrl(newBoard.id, viewMode));
    announce(`Created board ${name}`);
  }, [projectId, createBoardMutation, buildBoardUrl, viewMode, navigate]);

  const handleRefresh = useCallback(() => {
    if (projectId && boardId) {
      queryClient.invalidateQueries({ queryKey: ['board', projectId, boardId] });
      queryClient.invalidateQueries({ queryKey: ['issues', projectId, boardId] });
      queryClient.invalidateQueries({ queryKey: ['board-stats', projectId, boardId] });
      announce('Refreshing board');
    }
  }, [projectId, boardId, queryClient]);

  const handleCreateIssue = useCallback(() => {
    const title = prompt('Issue title:');
    if (!title || !projectId || !boardId) return;
    const type = prompt('Type (task/bug/story/epic):', 'task') as 'task' | 'bug' | 'story' | 'epic';
    createIssueMutation.mutate(
      { projectId, boardId, data: { title, type: type || 'task', status: 'backlog', priority: 'medium' } },
      {
        onSuccess: (newIssue) => announceIssueCreated(newIssue.id, type || 'task'),
        onError: () => {
          announceError('Failed to create issue');
          toast.error('Failed to create issue. Please try again.');
        },
      },
    );
  }, [projectId, boardId, createIssueMutation, toast]);

  const handleMoveIssue = useCallback((issueId: string, newStatus: string) => {
    if (!projectId || !boardId || !board) return;

    const validation = validateEpicMove(issueId, newStatus, issues, board.columns);
    if (!validation.allowed) {
      toast.warning(validation.reason);
      return;
    }

    const oldStatus = issues[issueId]?.status;
    moveIssueMutation.mutate(
      { projectId, boardId, issueId, status: newStatus },
      {
        onSuccess: () => {
          if (oldStatus) announceIssueStatusChange(issueId, oldStatus, newStatus);
        },
        onError: () => {
          announceError('Failed to move issue');
          toast.error('Failed to move issue. Please try again.');
        },
      },
    );
  }, [projectId, boardId, board, issues, moveIssueMutation, toast]);

  const handleIssueClick = useCallback((issueId: string) => {
    navigate(buildBoardUrl(boardId, viewMode, issueId));
    announceModalOpened('Issue details');
  }, [boardId, viewMode, buildBoardUrl, navigate]);

  const handleCloseDetail = useCallback(() => {
    navigate(buildBoardUrl(boardId, viewMode));
    announceModalClosed('Issue details');
  }, [boardId, viewMode, buildBoardUrl, navigate]);

  // ── Keyboard shortcuts ──
  useKeyboardNavigation({
    shortcuts: [
      { ...TRACKER_SHORTCUTS.NEW_ISSUE, action: handleCreateIssue },
      { ...TRACKER_SHORTCUTS.REFRESH, action: handleRefresh },
      {
        ...TRACKER_SHORTCUTS.CLOSE_PANEL,
        action: () => { if (selectedIssue) handleCloseDetail(); },
      },
      { ...TRACKER_SHORTCUTS.KANBAN_VIEW, action: () => handleViewChange('kanban') },
      { ...TRACKER_SHORTCUTS.LIST_VIEW, action: () => handleViewChange('list') },
      { ...TRACKER_SHORTCUTS.TIMELINE_VIEW, action: () => handleViewChange('timeline') },
      { ...TRACKER_SHORTCUTS.GANTT_VIEW, action: () => handleViewChange('gantt') },
    ],
  });

  // ── Loading: only block on project context. Everything else loads inline. ──
  if (projectsLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading board...</p>
        </div>
      </div>
    );
  }

  if (!projectId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-amber-500 text-5xl mb-4">📋</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">No Project Selected</h2>
          <p className="text-gray-600">Please select a project to use the board.</p>
        </div>
      </div>
    );
  }

  // Content area state — resolved after project is ready
  const dataLoading = boardsLoading || boardLoading || issuesLoading;

  const renderContent = () => {
    if (boardError) {
      return (
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Failed to Load Board</h2>
            <p className="text-gray-600 mb-4">{boardError instanceof Error ? boardError.message : 'Failed to load board'}</p>
            <button onClick={handleRefresh} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
              Retry
            </button>
          </div>
        </div>
      );
    }

    if (dataLoading) {
      return (
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
            <p className="text-sm text-gray-500">Loading issues...</p>
          </div>
        </div>
      );
    }

    if (!boardsLoading && boards.length === 0) {
      return (
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="text-amber-500 text-5xl mb-4">📋</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">No Boards Yet</h2>
            <p className="text-gray-600 mb-4">This project doesn't have any boards.</p>
          </div>
        </div>
      );
    }

    if (!board) {
      return (
        <div className="flex items-center justify-center flex-1">
          <p className="text-gray-600">No board data available</p>
        </div>
      );
    }

    return (
      <>
        {viewMode === 'kanban' && (
          <KanbanBoard
            board={board}
            issues={filteredIssues}
            allIssues={issues}
            onIssueClick={handleIssueClick}
            onIssueMove={handleMoveIssue}
            sortRules={display.sortRules}
            visibleFields={display.visibleFields}
          />
        )}

        {viewMode === 'list' && (
          <ListView
            issues={Object.values(filteredIssues)}
            onIssueClick={(issue) => handleIssueClick(issue.id)}
            searchQuery={filters.searchText}
            projectId={projectId}
            boardId={boardId}
            sortRules={display.sortRules}
          />
        )}

        {viewMode === 'gantt' && (
          <GanttView
            issues={filteredIssues}
            onIssueClick={handleIssueClick}
          />
        )}
      </>
    );
  };

  // ── Render: page shell always visible, content loads inline ──
  return (
    <PageLayout variant="full-width">
      {/* Skip to content link for keyboard navigation */}
      <a
        {...skipToContentProps}
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:rounded-lg focus:bg-white focus:text-gray-900 focus:shadow-lg"
        style={{ outline: '3px solid rgb(var(--c-coral-500, 217 127 111))' }}
      >
        Skip to main content
      </a>

      {/* Main Content Area — header renders immediately, content loads inline */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        <BoardHeader
          boards={boards}
          selectedBoardId={boardId}
          board={board}
          viewMode={viewMode}
          display={display}
          issues={issues}
          initiativesCount={initiatives.length}
          isMobile={isMobile}
          projectId={projectId}
          onBoardChange={handleBoardChange}
          onViewChange={handleViewChange}
          onCreateIssue={handleCreateIssue}
          onCreateBoard={() => setCreateBoardOpen(true)}
          onRefresh={handleRefresh}
        />

        {/* Filter Bar — stats are now in the header, no separate StatisticsBar */}
        <FilterBar
          filters={filters}
          onFiltersChange={setFilters}
          projectId={projectId}
          presetsSlot={
            <FilterPresetsDropdown
              presets={boardPresets}
              hasActiveFilters={hasBoardActiveFilters}
              onSave={saveBoardPreset}
              onApply={applyBoardPreset}
              onDelete={deleteBoardPreset}
            />
          }
        />

        {/* Views — loading/error/empty states are inline, not full-page */}
        <main
          id="main-board-content"
          className="flex-1 flex flex-col"
          role="main"
          aria-label="Issue board"
          tabIndex={-1}
        >
          {renderContent()}
        </main>

        {/* Issue Detail Panel */}
        {selectedIssue && issues[selectedIssue] && board && (
          <IssueDetailPanel
            issue={issues[selectedIssue]}
            board={board}
            allIssues={issues}
            onClose={handleCloseDetail}
            onStatusChange={handleMoveIssue}
          />
        )}
      </div>

      <CreateBoardDialog
        open={createBoardOpen}
        onClose={() => setCreateBoardOpen(false)}
        onCreate={handleCreateBoard}
      />
    </PageLayout>
  );
}
